/**
 * CommunityProfilePage — Unified profile view page at /profile/:uuid.
 *
 * Handles feature flag / auth / UUID gates, three-tier Apollo profile queries,
 * privacy gating, profile rendering (header, bio, identity cards), and
 * delegates playlist display to ProfilePlaylistsSection.
 */

import React, {
  lazy, Suspense, useEffect, useMemo, useRef, useState, useCallback,
} from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useMutation } from '@apollo/client'
import { Map } from 'immutable'
import { ErrorBoundary } from 'react-error-boundary'
import { usePostHogEvent } from '../../../../PostHog/index'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../../StaticText/StaticTextContext'
import { get as getConfig } from '../../../../../config/index'
import { selectAuth } from '../../../../../services/auth/selectors'
import usePrimaryAppLanguage from '../../../../../services/community/shared/usePrimaryAppLanguage'
import {
  MobileBreakpointProvider,
  COMMUNITY_BREAKPOINTS,
} from '../../../../../services/community/shared/MobileBreakpointContext'
import {
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../../../services/apollo-graphql/index'
import {
  GET_CURRENT_PROFILE,
  GET_PROFILE_BY_UUID,
  GET_PUBLIC_PROFILE_BY_UUID,
  GET_PROFILE_IN_PROGRESS_CAROUSEL,
  GET_PROFILE_WATCH_HISTORY_CAROUSEL,
  CREATE_FOLLOW,
  DELETE_FOLLOW,
} from '../../../../../services/community/query'
import { isProfileVisible, isFieldVisible } from '../../../../../services/community/index'
import { getMemberSinceYear } from '../../../../../services/community/identity/library/content-utils'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import { H3 } from '../../../../Heading/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import ErrorPage from '../../../../ErrorPage/index'
import { ERROR_TYPE_404 } from '../../../../ErrorPage/types'
import withCommunityErrorBoundary from '../../../shared/ErrorHandling/withCommunityErrorBoundary/index'
import ProfileArchetypesSection from '../ProfileArchetypesSection/index'
import ProfileAvatarRing from '../../shared/ProfileAvatarRing/index'
import ProfileGradientHeader from '../../shared/ProfileGradientHeader/index'
import ProfileShareButton from '../../shared/ProfileShareButton/index'
import ProfileJourneyStageSection from '../ProfileJourneyStageSection/index'
import ProfileMetaLine from '../../shared/ProfileMetaLine/index'
import ProfilePlaylistsSection from '../ProfilePlaylistsSection/index'
import ProfileReviewBanner from '../ProfileReviewBanner/index'
import useInProgressEnrichment from '../../../../../services/community/identity/library/useInProgressEnrichment'
import useTitleGroupHydration from '../../../../../services/community/identity/library/useTitleGroupHydration'
import { enrichWatchHistoryItems } from '../../../../../services/community/identity/library/watchHistoryEnrichment'

import type {
  GetCurrentProfileResponse,
  GetProfileByUidResponse,
  GetPublicProfileResponse,
  GetProfileInProgressCarouselResponse,
  GetProfileWatchHistoryCarouselResponse,
  FollowResponse,
  UnfollowResponse,
  StaticTextMap,
} from '../../../../../services/community/types'

// Re-use the ByUid response shapes for UUID queries (same return type)
type GetProfileByUuidResponse = { userProfileByUuid: GetProfileByUidResponse['userProfileByUid'] }
type GetPublicProfileByUuidResponse = { userProfilePublicByUuid: GetPublicProfileResponse['userProfilePublic'] }

const ConnectionsModal = lazy(() => import('../../shared/ConnectionsModal/index'))

// Lazy-load icons to avoid SSR crash: Node has no require.extensions handler for .svg
const GaiaFlower = lazy(() => import('../../../../Icon/icons/GaiaFlower/index'))
const PencilEditIcon = lazy(() => import('../../../../Icon/icons/PencilEdit/index'))
const UserFollowIcon = lazy(() => import('../../../../Icon/icons/UserFollow/index'))
const UsersIcon = lazy(() => import('../../../../Icon/icons/Users/index'))

const PROFILE_UUID_REGEX = /^\/profile\/(.+)$/

function extractUuidFromPath (location: { pathname?: string }): string | null {
  const pathname = location && location.pathname ? location.pathname : ''
  const match = pathname.match(PROFILE_UUID_REGEX)
  return match ? match[1] : null
}

export interface CommunityProfilePageProps {
  history: { push: (path: string) => void }
  location: { pathname?: string; search?: string }
  staticText: StaticTextMap
}

// Inner renders all UI copy via `useTranslatedText` hooks; the hooks resolve
// against the Provider that the Outer CommunityProfilePage wrapper (defined
// below) installs with the `communityProfilePage` slice. ProfilePlaylistsSection
// also reads watchLater/favorites from that slice via the same hook, so no
// `staticText` prop needs to be threaded through this Inner.
function CommunityProfilePageInner ({
  history,
  location,
}: CommunityProfilePageProps) {
  const config = getConfig()
  const isCommunityProfileEnabled = !!config?.features?.communityProfile

  const auth = useSelector(selectAuth)
  const user = useSelector((state: { user: Map<string, unknown> }) => state.user)

  const profileUuid = extractUuidFromPath(location)
  const loggedInUuid = auth.get('uuid')
  const isLoggedIn = !!loggedInUuid
  const jwt = auth.get('jwt') as string | undefined
  const language = usePrimaryAppLanguage(user)

  const isOwner = profileUuid && loggedInUuid
    ? profileUuid === loggedInUuid
    : false

  // ── Apollo queries for profile data ───────────────────────────────────
  // NOTE: All hooks (useQuery, useMemo) must be above any conditional
  // returns to satisfy the Rules of Hooks. Profile resolution and data
  // extraction that depend on the query results happen after the hooks.
  //
  // Phase 3b: the main profile query renders scalars + favorites/watchLater
  // meta only — `includeMemberFields=false` and `includeWatchHistory=false`
  // skip the heavy `inProgress` and `watchHistory` arrays via @include in
  // USER_PROFILE_SOCIAL_FIELDS. The In Progress + Watch History carousels
  // fire their own queries in parallel (`GET_PROFILE_IN_PROGRESS_CAROUSEL`
  // / `GET_PROFILE_WATCH_HISTORY_CAROUSEL`) and surface their own loading
  // states inside the carousel skeletons. The page-paint gate keys off
  // the main profile query only.
  const {
    data: currentProfileData,
    loading: currentProfileLoading,
  } = useQuery<GetCurrentProfileResponse>(GET_CURRENT_PROFILE, {
    variables: {
      language,
      contentLanguage: [language],
      includeMemberFields: false,
      includeProfileActivity: true,
      includeWatchHistory: false,
    },
    skip: !isOwner || !isLoggedIn,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const {
    data: memberProfileData,
    loading: memberProfileLoading,
  } = useQuery<GetProfileByUuidResponse>(GET_PROFILE_BY_UUID, {
    variables: {
      uuid: profileUuid,
      language,
      contentLanguage: [language],
      includeMemberFields: false,
      includeProfileActivity: true,
      includeWatchHistory: false,
    },
    skip: isOwner || !isLoggedIn || !profileUuid,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const {
    data: publicProfileData,
    loading: publicProfileLoading,
  } = useQuery<GetPublicProfileByUuidResponse>(GET_PUBLIC_PROFILE_BY_UUID, {
    // Anonymous viewers cannot resolve inProgress/watchHistory; @include
    // skips them in the fragment via the same gates passed below. They
    // can see public favorites/watchLater meta (includeProfileActivity=true).
    variables: {
      uuid: profileUuid,
      language,
      contentLanguage: [language],
      includeMemberFields: false,
      includeProfileActivity: true,
      includeWatchHistory: false,
    },
    skip: isLoggedIn || !profileUuid,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
  })

  // ── Carousel queries (fire in parallel with main; only when isLoggedIn) ──
  // Anonymous viewers never resolve inProgress / watchHistory (auth-gated
  // resolvers), so we skip these for the public-viewer path.
  const {
    data: inProgressCarouselData,
    error: inProgressCarouselError,
  } = useQuery<GetProfileInProgressCarouselResponse>(GET_PROFILE_IN_PROGRESS_CAROUSEL, {
    variables: {
      uuid: profileUuid,
      contentLanguage: [language],
    },
    skip: !isLoggedIn || !profileUuid,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const {
    data: watchHistoryCarouselData,
    error: watchHistoryCarouselError,
  } = useQuery<GetProfileWatchHistoryCarouselResponse>(GET_PROFILE_WATCH_HISTORY_CAROUSEL, {
    variables: {
      uuid: profileUuid,
      contentLanguage: [language],
    },
    skip: !isLoggedIn || !profileUuid,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  // Carousels are configured with errorPolicy:'all', so a federated resolver
  // failure leaves the queries resolving with empty data — indistinguishable
  // from "no activity yet" without the error half of the response.  Wire a
  // PostHog signal so the silent-failure mode is observable; the in-DOM
  // error UI is rendered by ProfilePlaylistsSection from these error props.
  const trackCarouselError = usePostHogEvent('community_carousel_error')
  const trackedInProgressError = useRef(false)
  const trackedWatchHistoryError = useRef(false)

  useEffect(() => {
    if (inProgressCarouselError && !trackedInProgressError.current) {
      trackedInProgressError.current = true
      trackCarouselError('in_progress', inProgressCarouselError.message)
    }
  }, [inProgressCarouselError, trackCarouselError])

  useEffect(() => {
    if (watchHistoryCarouselError && !trackedWatchHistoryError.current) {
      trackedWatchHistoryError.current = true
      trackCarouselError('watch_history', watchHistoryCarouselError.message)
    }
  }, [watchHistoryCarouselError, trackCarouselError])

  // ── Resolve profile data (before any conditional returns) ──────────────
  let isLoading: boolean
  let profile: GetCurrentProfileResponse['userProfileCurrent']
    | GetProfileByUuidResponse['userProfileByUuid']
    | GetPublicProfileByUuidResponse['userProfilePublicByUuid']
  if (isOwner) {
    isLoading = currentProfileLoading
    profile = currentProfileData?.userProfileCurrent
  } else if (isLoggedIn) {
    isLoading = memberProfileLoading
    profile = memberProfileData?.userProfileByUuid
  } else {
    isLoading = publicProfileLoading
    profile = publicProfileData?.userProfilePublicByUuid
  }
  const hasData = !!profile

  // ── Privacy gating (computed before returns, used after) ──────────────
  const profileVisibility = profile ? (profile.profileVisibility || 'public') : 'public'
  const viewerContext = profile ? profile.viewerContext : undefined
  const defaultVisLevel = isLoggedIn ? 'member' : 'public'
  const rawVisLevel = viewerContext
    ? viewerContext.visibilityLevel || defaultVisLevel
    : defaultVisLevel
  // Always evaluate as 'member' so owners see what others see:
  const visibilityLevel = rawVisLevel === 'owner' ? 'member' : rawVisLevel
  const profileIsVisible = hasData && isProfileVisible({ profileVisibility, visibilityLevel })

  // ── Hooks that must run unconditionally (Rules of Hooks) ──────────────
  const archetypeRaw = profile ? profile.archetype : ''
  const archetypes = useMemo(() => {
    if (!archetypeRaw || typeof archetypeRaw !== 'string') return [] as string[]
    return archetypeRaw.split(',').map((s: string) => s.trim()).filter(Boolean)
  }, [archetypeRaw])

  const createdAt = profile ? (profile.createdAt || '') : ''
  const memberYear = useMemo(() => getMemberSinceYear(createdAt) || '', [createdAt])

  const trackConnectionsViewed = usePostHogEvent('community_connections_viewed')
  // Member-scoped equivalent — fires only when the viewer opens *another*
  // member's connections modal. Coexists with the generic event for back-compat.
  const trackMemberConnectionsViewed = usePostHogEvent('community_member_connections_viewed')
  const connectionsTargetDisplayName = profile?.displayName || undefined
  const [connectionsTab, setConnectionsTab] = useState<'followers' | 'following' | 'discover' | null>(null)
  const handleOpenFollowers = useCallback(() => {
    trackConnectionsViewed('followers')
    if (!isOwner) {
      trackMemberConnectionsViewed('followers', connectionsTargetDisplayName)
    }
    setConnectionsTab('followers')
  }, [trackConnectionsViewed, trackMemberConnectionsViewed, isOwner, connectionsTargetDisplayName])
  const handleOpenFollowing = useCallback(() => {
    trackConnectionsViewed('following')
    if (!isOwner) {
      trackMemberConnectionsViewed('following', connectionsTargetDisplayName)
    }
    setConnectionsTab('following')
  }, [trackConnectionsViewed, trackMemberConnectionsViewed, isOwner, connectionsTargetDisplayName])
  const handleCloseConnections = useCallback(() => setConnectionsTab(null), [])

  // ── Activity surfaces (In Progress, Watch History) ──────────────────
  // Sourced from the per-carousel queries that fire in parallel with the
  // main profile query (Phase 3b — see comment near useQuery calls above).
  // Both `inProgress` and `watchHistory` are auth-gated server-side, so
  // the carousel queries are skipped for anonymous viewers; the enrichment
  // hooks tolerate undefined source data and produce empty arrays.
  const inProgress = inProgressCarouselData?.userProfileByUuid?.inProgress
  const watchHistory = watchHistoryCarouselData?.userProfileByUuid?.watchHistory
  // language + jwt thread into the enrichment hooks so they can hydrate
  // each entry's full Content via useContentHydration — PR 2 removed
  // `featureMedia.mediaAssets.aiMetadata.learnerLevel` from these
  // carousel queries; hydration restores the difficulty pill.
  // useInProgressEnrichment returns `{ items, isHydrating }`. The profile
  // page's Currently Watching surface is read-only (no skeleton path wired
  // here today), so we only consume `items` and rely on the carousel
  // query's loading state for gating elsewhere on the page.
  const { items: inProgressItemsEnriched } = useInProgressEnrichment(inProgress, language, jwt)
  // Mirror the watch-history hydration that useWatchHistoryData runs
  // internally — the profile page fetches via GET_PROFILE_WATCH_HISTORY_CAROUSEL
  // rather than GET_USER_HISTORY, so we drive the contentMap directly here
  // and pass it into the pure enrichWatchHistoryItems transform.
  const watchHistoryHydrationItems = useMemo(() => {
    if (!watchHistory) return []
    const seen = new Set<string>()
    return watchHistory.reduce<Array<{ contentId: string; contentType: string }>>(
      (acc, entry) => {
        const featured = entry?.media?.contentFeatured?.[0]
        const cid = featured?.contentId != null ? String(featured.contentId) : ''
        const ctype = featured?.contentType ? String(featured.contentType) : ''
        if (!cid || !ctype) return acc
        const key = `${cid}-${ctype}`
        if (seen.has(key)) return acc
        seen.add(key)
        acc.push({ contentId: cid, contentType: ctype })
        return acc
      },
      [],
    )
  }, [watchHistory])
  // Per-row hydration via useTitleGroupHydration. Items here lack
  // `titleGroupId` (GET_PROFILE_WATCH_HISTORY_CAROUSEL doesn't select it
  // on `media.contentFeatured`), so the `${contentId}-${contentType}`
  // fallback is mandatory. `contentId` + `contentType` are already
  // String-coerced inside the reducer above (L319-320).
  const watchHistoryTitleGroupIds = useMemo(
    () => watchHistoryHydrationItems.map(
      ({ contentId, contentType }) => `${contentId}-${contentType}`,
    ),
    [watchHistoryHydrationItems],
  )
  const { contentMap: watchHistoryContentMap } = useTitleGroupHydration(
    watchHistoryTitleGroupIds,
    language,
    { skip: watchHistoryTitleGroupIds.length === 0, jwt },
  )
  const watchHistoryItemsEnriched = useMemo(
    () => enrichWatchHistoryItems(watchHistory ?? [], watchHistoryContentMap),
    [watchHistory, watchHistoryContentMap],
  )

  // ── Follow / Unfollow mutation ──────────────────────────────────────
  // Follow/unfollow mutations still use numeric uid — source from loaded
  // profile data (UserProfile.uid remains available server-side).
  const profileUid = profile?.uid || null
  const viewerIsFollowing = profile?.viewerContext?.isFollowing ?? false
  const [followUser] = useMutation<FollowResponse>(CREATE_FOLLOW, {
    variables: { uid: profileUid },
    context: { jwt },
  })
  const [unfollowUser] = useMutation<UnfollowResponse>(DELETE_FOLLOW, {
    variables: { uid: profileUid },
    context: { jwt },
  })
  // Only rendered behind an isLoggedIn gate (both in the visible-profile and
  // HiddenProfileView paths), so no need to re-check auth here — the
  // mutations will run with the viewer's jwt from context.
  // Member-scoped follow events — only fire on actions against *another*
  // member's profile (the follow button doesn't render for the owner, so
  // this surface is intrinsically other-only). Renamed from
  // `community_profile_followed/_unfollowed` for naming clarity; old events
  // retain only historical data in PostHog.
  const trackMemberProfileFollowed = usePostHogEvent('community_member_profile_followed')
  const trackMemberProfileUnfollowed = usePostHogEvent('community_member_profile_unfollowed')
  // Target display name for PostHog payloads — never capture the target's UUID.
  const targetDisplayName = profile?.displayName || undefined
  const handleFollowToggle = useCallback(async () => {
    try {
      if (viewerIsFollowing) {
        await unfollowUser()
        trackMemberProfileUnfollowed(targetDisplayName)
      } else {
        await followUser()
        trackMemberProfileFollowed(targetDisplayName)
      }
    } catch {
      // Stay in current state on failure — Apollo cache will not update
    }
  }, [
    viewerIsFollowing,
    followUser,
    unfollowUser,
    targetDisplayName,
    trackMemberProfileFollowed,
    trackMemberProfileUnfollowed,
  ])

  // Profile-share tracking — fires from the ProfileShareButton via its
  // `onShared` callback prop. The button itself is presentational (no
  // PostHog hook) to keep its unit tests Provider-free; analytics live here
  // where the Redux Provider is already in scope.
  const trackProfileShared = usePostHogEvent('community_profile_shared')
  const handleProfileShared = useCallback((method: 'copy_link') => {
    trackProfileShared(method, targetDisplayName)
  }, [trackProfileShared, targetDisplayName])

  // ── Page-view tracking (PostHog session-recording trigger) ─────────────
  // Fires once per mount after profile data settles.  A ref guard makes this
  // resilient to React strict-mode double-effects and Apollo cache updates
  // that re-render the page.
  //
  // Two events fire on a non-owner view: the generic `community_profile_viewed`
  // (kept for back-compat with existing dashboards) AND the member-scoped
  // `community_member_profile_viewed` (clean signal for "viewing another
  // member's profile" without `is_own_profile=false` filtering). Owner views
  // fire only the generic event.
  const trackProfileViewed = usePostHogEvent('community_profile_viewed')
  const trackMemberProfileViewed = usePostHogEvent('community_member_profile_viewed')
  const hasTrackedProfileView = useRef(false)
  useEffect(() => {
    if (hasTrackedProfileView.current) return
    if (!profileUuid) return
    if (!hasData) return
    hasTrackedProfileView.current = true
    trackProfileViewed(!!isOwner, isLoggedIn, targetDisplayName)
    if (!isOwner) {
      trackMemberProfileViewed(isLoggedIn, targetDisplayName, viewerIsFollowing)
    }
  }, [
    profileUuid,
    hasData,
    isOwner,
    isLoggedIn,
    targetDisplayName,
    viewerIsFollowing,
    trackProfileViewed,
    trackMemberProfileViewed,
  ])

  // ── Translated UI copy ────────────────────────────────────────────────
  // Resolved via the surrounding <StaticTextProvider> (set up by the Outer
  // CommunityProfilePage wrapper below). All hooks must be declared before
  // the conditional gate returns so call order stays stable across renders.
  const gaiaMemberFallback = useTranslatedText({ keys: ['gaiaMember'] })
  const friendOfGaiaFallback = useTranslatedText({ keys: ['friendOfGaia'] })
  const editButtonLabel = useTranslatedText({ keys: ['editButton'] })
  const shareButtonLabel = useTranslatedText({ keys: ['shareButton'] })
  const copyErrorMessage = useTranslatedText({ keys: ['copyError'] })
  const followingActionLabel = useTranslatedText({ keys: ['following'] })
  const followActionLabel = useTranslatedText({ keys: ['follow'] })
  const followerSingularLabel = useTranslatedText({ keys: ['followerSingular'] })
  const followersPluralLabel = useTranslatedText({ keys: ['followersLabel'] })
  const followingCountLabel = useTranslatedText({ keys: ['followingLabel'] })
  const memberSinceTemplate = useTranslatedText({ keys: ['memberSinceYear'] })
  const locationLabel = useTranslatedText({ keys: ['locationLabel'] })
  const birthdayLabel = useTranslatedText({ keys: ['birthdayLabel'] })
  const topicsLabel = useTranslatedText({ keys: ['topics'] })

  // ── Gate checks (conditional returns below — no hooks after this) ─────
  if (!isCommunityProfileEnabled) {
    return (
      <ErrorPage
        history={history}
        location={location}
        code={ERROR_TYPE_404}
      />
    )
  }

  if (!profileUuid) {
    return (
      <ErrorPage
        history={history}
        location={location}
        code={ERROR_TYPE_404}
      />
    )
  }

  if (isLoading && !hasData) {
    return (
      <div className="community-profile-page community-profile-page--loading">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  // ── No-data fallback: render hidden-profile view ──────────────────────
  if (!hasData) {
    if (!isOwner) {
      // Non-owner (logged-in OR logged-out) with no data: backend returned null
      // for a private profile, or the UID is unknown. Render HiddenProfileView
      // so visibility parity holds across auth states and UID existence is not
      // leaked via a 404.
      const fallbackDisplayName = gaiaMemberFallback
      return (
        <div className="community-profile-page">
          <HiddenProfileView
            displayName={fallbackDisplayName}
            avatarUrl=""
            isLoggedIn={isLoggedIn}
            isOwner={false}
            loggedInUuid={loggedInUuid as string | undefined}
          />
        </div>
      )
    }
    if (isOwner && !isLoading) {
      const fallbackDisplayName = gaiaMemberFallback
      return (
        <div className="community-profile-page">
          <HiddenProfileView
            displayName={fallbackDisplayName}
            avatarUrl=""
            isLoggedIn={isLoggedIn}
            isOwner
            loggedInUuid={loggedInUuid as string | undefined}
          />
        </div>
      )
    }
    return (
      <div className="community-profile-page community-profile-page--loading">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  // ── From here, profile is guaranteed to exist ─────────────────────────
  const displayName = profile.displayName || friendOfGaiaFallback
  const avatarUrl = profile.avatar?.card || ''

  if (!profileIsVisible) {
    return (
      <div className="community-profile-page">
        <HiddenProfileView
          displayName={displayName}
          avatarUrl={avatarUrl}
          isLoggedIn={isLoggedIn}
          isOwner={isOwner}
          viewerIsFollowing={viewerIsFollowing}
          onFollowToggle={handleFollowToggle}
          loggedInUuid={loggedInUuid as string | undefined}
        />
      </div>
    )
  }

  // ── Profile data extraction ───────────────────────────────────────────
  // The backend nulls *Visibility fields for non-owners and nulls the data
  // field itself when it should be hidden. So for non-owner viewers (logged-in
  // member or logged-out public) we receive `field="value", fieldVisibility=null`
  // for visible fields and `field=null, fieldVisibility=null` for hidden ones.
  // Defaulting `*Visibility` to 'private' here would hide data the backend
  // intends to show; defaulting to undefined lets isFieldVisible return true
  // unless the (owner-preview) case explicitly has a 'private'/'membersOnly'.
  const profileLocation = profile.location || ''
  const birthday = profile.birthday || ''
  const { locationVisibility } = profile
  const { birthdayVisibility } = profile
  const { memberSinceVisibility } = profile
  const { topicsVisibility } = profile
  const archetypesVisibility = profile.archetypeVisibility
  const journeyStage: string | null = profile.yourJourney || null
  const journeyStageVisibility = profile.yourJourneyVisibility
  const bio = profile.bio || ''
  const { bioVisibility } = profile
  const showBio = !!bio && isFieldVisible(bioVisibility, visibilityLevel)
  const showJourneyStage = !!journeyStage
    && isFieldVisible(journeyStageVisibility, visibilityLevel)
  const topics = profile.topics || []
  const showLocation = profileLocation && isFieldVisible(locationVisibility, visibilityLevel)
  const showBirthday = birthday && isFieldVisible(birthdayVisibility, visibilityLevel)
  const showMemberSince = isFieldVisible(memberSinceVisibility, visibilityLevel)
  const showTopics = isFieldVisible(topicsVisibility, visibilityLevel)
  const showArchetypes = archetypes.length > 0
    && isFieldVisible(archetypesVisibility, visibilityLevel)

  const followerCount = profile.followerCount || 0
  const followingCount = profile.followingCount || 0

  // ── Render: visible profile ───────────────────────────────────────────
  return (
    <div className="community-profile-page">
      <div className="community-profile-member">
          <ProfileGradientHeader
            className="community-profile-member__gradient-header"
            avatarContent={(
              <ProfileAvatarRing avatarUrl={avatarUrl} displayName={displayName} />
            )}
            displayName={displayName}
            actionButtons={(() => {
              if (isOwner) {
                return (
                  <>
                    <Button
                      type={BUTTON_TYPES.GHOST}
                      className="profile-gradient-header__action-btn profile-gradient-header__edit-btn"
                      url="/profile/edit"
                    >
                      <Suspense fallback={null}>
                        <PencilEditIcon size="16px" />
                      </Suspense>
                      {editButtonLabel}
                    </Button>
                    <ProfileShareButton
                      uid={String(loggedInUuid)}
                      label={shareButtonLabel}
                      errorMessage={copyErrorMessage}
                      onShared={handleProfileShared}
                    />
                  </>
                )
              }
              // Non-owner + logged-out: no Follow button (the mutation is
              // @auth-gated; we don't bounce anonymous visitors through /login
              // from a profile view). Logged-in non-owners get the toggle.
              if (!isLoggedIn) return null
              return (
                <Button
                  type={BUTTON_TYPES.GHOST}
                  className={`profile-gradient-header__action-btn profile-gradient-header__follow-btn${viewerIsFollowing ? ' profile-gradient-header__follow-btn--following' : ''}`}
                  onClick={handleFollowToggle}
                >
                  <Suspense fallback={null}>
                    {viewerIsFollowing
                      ? <UsersIcon size="16px" />
                      : <UserFollowIcon size="16px" />}
                  </Suspense>
                  {viewerIsFollowing ? followingActionLabel : followActionLabel}
                </Button>
              )
            })()}
            socialLine={(
              <span className="profile-gradient-header__social-counts">
                {isLoggedIn ? (
                  <Button
                    type={BUTTON_TYPES.LINK}
                    className="profile-gradient-header__social-link"
                    onClick={handleOpenFollowers}
                  >
                    {followerCount}
                    {' '}
                    {followerCount === 1 ? followerSingularLabel : followersPluralLabel}
                  </Button>
                ) : (
                  <span className="profile-gradient-header__social-link">
                    {followerCount}
                    {' '}
                    {followerCount === 1 ? followerSingularLabel : followersPluralLabel}
                  </span>
                )}
                <span className="profile-gradient-header__social-dot" aria-hidden="true" />
                {isLoggedIn ? (
                  <Button
                    type={BUTTON_TYPES.LINK}
                    className="profile-gradient-header__social-link"
                    onClick={handleOpenFollowing}
                  >
                    {followingCount}
                    {' '}
                    {followingCountLabel}
                  </Button>
                ) : (
                  <span className="profile-gradient-header__social-link">
                    {followingCount}
                    {' '}
                    {followingCountLabel}
                  </span>
                )}
              </span>
            )}
            metaLine={(
              <ProfileMetaLine
                memberYear={memberYear}
                memberSinceLabel={memberSinceTemplate}
                showMemberSince={showMemberSince}
                location={profileLocation}
                locationLabel={locationLabel}
                showLocation={!!showLocation}
                birthday={birthday}
                birthdayLabel={birthdayLabel}
                showBirthday={!!showBirthday}
              />
            )}
            bio={bio}
            showBio={showBio}
          />

          {/* Identity cards — single 3-column row (Journey, Archetypes, Topics)
              at all breakpoints per Figma "Profile pages - current" spec. */}
          <div className="community-profile-member__card--view">
            {(showArchetypes || (topics.length > 0 && showTopics) || showJourneyStage) ? (
              <div className="community-profile-member__identity-row">
                {showJourneyStage ? (
                  <div className="community-profile-member__identity-card community-profile-member__identity-card--journey">
                    <ProfileJourneyStageSection
                      journeyStage={journeyStage}
                      journeyStageVisibility={journeyStageVisibility}
                      isOwner={isOwner}
                    />
                  </div>
                ) : null}
                {showArchetypes ? (
                  <div className="community-profile-member__identity-card community-profile-member__identity-card--archetypes">
                    <ProfileArchetypesSection
                      archetypes={archetypes}
                      archetypesVisibility={archetypesVisibility}
                      isOwner={isOwner}
                    />
                  </div>
                ) : null}
                {topics.length > 0 && showTopics ? (
                  <div className="community-profile-member__identity-card community-profile-member__identity-card--topics">
                    <H3 className="community-profile-member__identity-heading">
                      {topicsLabel}
                    </H3>
                    <div className="community-profile-member__topic-pills-wrap">
                      {topics.map((topic: { contentId: number; title: string }) => (
                        <span key={topic.contentId} className="community-profile-member__topic-pill">
                          {topic.title}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Unified library section: Currently Watching → Watch Later →
                Favorites → custom playlists → Watch History, all inside one
                community-profile-member__section with a single search input. */}
            <ProfilePlaylistsSection
              uid={profileUid}
              language={language}
              isOwner={isOwner}
              isLoggedIn={isLoggedIn}
              profileVisibility={profileVisibility}
              visibilityLevel={visibilityLevel}
              inProgressItems={inProgressItemsEnriched}
              watchHistoryItems={watchHistoryItemsEnriched}
              inProgressError={inProgressCarouselError}
              watchHistoryError={watchHistoryCarouselError}
              currentlyWatchingVisibility={profile.currentlyWatchingVisibility}
              watchHistoryVisibility={profile.watchHistoryVisibility}
            />
          </div>
        </div>
      {isLoggedIn && connectionsTab && profileUid ? (
        <Suspense fallback={null}>
          <ConnectionsModal
            uid={profileUid}
            initialTab={connectionsTab}
            onClose={handleCloseConnections}
            jwt={jwt}
            displayName={displayName}
            isViewerProfile={!!isOwner}
            viewerUuid={loggedInUuid as string | undefined}
          />
        </Suspense>
      ) : null}
    </div>
  )
}

// ── Hidden profile sub-view ───────────────────────────────────────────────
// Renders the private/hidden profile: gradient header + poetic message + lotus.
//
// When called from the !profileIsVisible code path (profile data exists but is
// private), pass isOwner / viewerIsFollowing / onFollowToggle so the header
// can render the appropriate action buttons:
//   • isOwner=true  → Share + Edit buttons (Edit is a Button with `url=`, no
//     `history.push` wiring needed)
//   • isOwner=false + isLoggedIn=true → Follow button
//
// When called from !hasData fallback paths, omit those props — the header
// will render without action buttons since we don't have a known profile.

interface HiddenProfileViewProps {
  displayName: string
  avatarUrl: string
  isLoggedIn: boolean
  /** Pass true when the viewer owns this profile */
  isOwner?: boolean
  /** Pass true if the viewer is already following this profile */
  viewerIsFollowing?: boolean
  /** Follow/unfollow handler — required to show the Follow button */
  onFollowToggle?: () => Promise<void>
  /** Logged-in viewer's UUID — used to build the "View your Profile" CTA
   *  link on the otherProfile-variant ProfileReviewBanner so the viewer
   *  lands on their own profile page. Optional: omitted for anonymous
   *  callers (isLoggedIn=false), where the banner isn't rendered. */
  loggedInUuid?: string
}

function HiddenProfileView ({
  displayName,
  avatarUrl,
  isLoggedIn,
  isOwner,
  viewerIsFollowing,
  onFollowToggle,
  loggedInUuid,
}: HiddenProfileViewProps) {
  const editLabel = useTranslatedText({ keys: ['editButton'] })
  const followingLabel = useTranslatedText({ keys: ['following'] })
  const followLabel = useTranslatedText({ keys: ['follow'] })
  const privateProfileLine1 = useTranslatedText({ keys: ['privateProfileLine1'] })
  const privateProfileLine2 = useTranslatedText({ keys: ['privateProfileLine2'] })

  // ── Action buttons for the gradient header ────────────────────────────
  let actionButtons: React.ReactNode
  if (isOwner) {
    actionButtons = (
      <Button
        type={BUTTON_TYPES.GHOST}
        className="profile-gradient-header__action-btn profile-gradient-header__edit-btn"
        url="/profile/edit"
      >
        <Suspense fallback={null}>
          <PencilEditIcon size="16px" />
        </Suspense>
        {editLabel}
      </Button>
    )
  } else if (!isOwner && isLoggedIn && onFollowToggle) {
    actionButtons = (
      <Button
        type={BUTTON_TYPES.GHOST}
        className={`profile-gradient-header__action-btn profile-gradient-header__follow-btn${viewerIsFollowing ? ' profile-gradient-header__follow-btn--following' : ''}`}
        onClick={onFollowToggle}
      >
        <Suspense fallback={null}>
          <UserFollowIcon size="16px" />
        </Suspense>
        {viewerIsFollowing ? followingLabel : followLabel}
      </Button>
    )
  }

  return (
    <div className="community-profile-member community-profile-member--hidden">
      <ProfileGradientHeader
        className="community-profile-member__gradient-header"
        avatarContent={(
          <ProfileAvatarRing avatarUrl={avatarUrl} displayName={displayName} />
        )}
        displayName={displayName}
        actionButtons={actionButtons}
      />
      <div className="community-profile-member__hidden-content">
        <div className="community-profile-member__hidden-message-row">
          <p className="community-profile-member__hidden-poetic">
            {privateProfileLine1}
            <br />
            {privateProfileLine2}
          </p>
          <ErrorBoundary fallback={null}>
            <Suspense fallback={null}>
              <GaiaFlower className="community-profile-member__lotus" />
            </Suspense>
          </ErrorBoundary>
        </div>
        {isLoggedIn ? (
          <ProfileReviewBanner
            variant={isOwner ? 'ownProfile' : 'otherProfile'}
            buttonTo={isOwner ? '/profile/edit' : `/profile/${loggedInUuid}`}
          />
        ) : null}
      </div>
    </div>
  )
}

// Outer: thin wrapper. Receives `staticText` from connectStaticText and
// provides it to the subtree so Inner's `useTranslatedText` hooks resolve
// against the `communityProfilePage` slice. Per Oracle pattern (see
// PlaylistSharedView → PlaylistSharedViewInner for the in-repo precedent),
// the boundary component does not consume its own slice — that's Inner's
// job — because the Provider only exists inside the return value.
function CommunityProfilePage (props: CommunityProfilePageProps) {
  return (
    <MobileBreakpointProvider breakpoints={COMMUNITY_BREAKPOINTS}>
      <StaticTextProvider staticText={props.staticText}>
        <CommunityProfilePageInner {...props} />
      </StaticTextProvider>
    </MobileBreakpointProvider>
  )
}

const CommunityProfilePageWithBoundary = withCommunityErrorBoundary(CommunityProfilePage)

export default connectStaticText({ storeKey: 'communityProfilePage' })(CommunityProfilePageWithBoundary)
